import { FilterT, FilterVariantE, IBasePayload, IFilter, IRequestPagination, ISort, SortT } from './api.interfaces'
import { IDropdownOption } from 'primsell-shared-layer/types'

export type ITransformPayload = IBasePayload<string, string>

export type TransformPayloadReturnT = Record<string, string> | null

export const transformFilters: (filters: FilterT<string>) => Record<string, string> = (filters) => {
  const filterResult: Record<string, string> = {}
  filters?.forEach((filterItem) => {
    if (filterItem.isDate && Array.isArray(filterItem.value)) {
      const from = filterItem.value[0]
      const to = filterItem.value[1]
      if (from && to) {
        return (filterResult[`filter[${filterItem.id}]`] = `${from}<>${to}`)
      }
      if (from) {
        return (filterResult[`filter[${filterItem.id}]`] = `>=${from}`)
      }
      if (to) {
        return (filterResult[`filter[${filterItem.id}]`] = `<=${to}`)
      }
    } else {
      if (typeof filterItem.value === 'string') {
        if (filterItem.filterVariant === FilterVariantE.EQUAL) {
          return (filterResult[`filter[${filterItem.id}]`] = filterItem.value)
        }
        if (filterItem.filterVariant === FilterVariantE.LIKE) {
          return (filterResult[`filter[${filterItem.id}]`] = `*${filterItem.value}*`)
        }
      }
      if (filterItem.filterVariant === FilterVariantE.IN) {
        if (Array.isArray(filterItem.value)) {
          return (filterResult[`filter[${filterItem.id}]`] = filterItem.value.join(','))
        }
        return (filterResult[`filter[${filterItem.id}]`] = filterItem.value)
      }
    }
  })
  return filterResult
}

export const transformSort: (sort: SortT<string>) => Record<string, string> = (sort) => {
  const sortResult: Record<string, string> = {}

  if (sort?.propertyName) {
    sortResult[`sort[${sort?.propertyName}]`] = sort?.sortType
  }

  return sortResult
}

export const transformPagination: (pagination: IRequestPagination) => Record<string, string> = (pagination) => {
  const paginationResult: Record<string, string> = {}

  paginationResult['limit'] = pagination.limit.toString()
  paginationResult['offset'] = pagination.offset.toString()

  return paginationResult
}

export const transformPayload = (payload: ITransformPayload): TransformPayloadReturnT => {
  let result: Record<string, string> = {}

  if (payload.filters) {
    result = { ...result, ...transformFilters(payload.filters) }
  }

  if (payload.sort) {
    result = { ...result, ...transformSort(payload.sort) }
  }

  if (payload.pagination) {
    result = { ...result, ...transformPagination(payload.pagination) }
  }

  return result
}

const convertValueToString = (value: string | string[]): string => {
  if (Array.isArray(value)) {
    return value.join(',')
  }
  return value
}

export const updateFilterValue = <T>({
  prev,
  id,
  value,
  filterVariant,
  listOfIds,
  isDate = false,
}: {
  prev: IBasePayload<T, unknown>
  id: T
  value: string | string[]
  filterVariant: FilterVariantE
  listOfIds?: string[]
  isDate?: boolean
}): FilterT<T> => {
  let updatedFilters = prev.filters ? [...prev.filters] : []

  if (!id) {
    return updatedFilters
  }

  if (Array.isArray(value) && value.length === 0) {
    return updatedFilters.filter((filter) => filter.id !== id)
  }

  const newFilter: IFilter<T> = {
    id,
    filterVariant,
    isDate,
    value: isDate ? value : convertValueToString(value),
  }

  const filterIndex = updatedFilters.findIndex((filter) => filter.id === id)

  if (filterIndex === -1) {
    updatedFilters = updatedFilters.filter((filter) => !listOfIds?.includes(filter.id as string))
    updatedFilters.push(newFilter)
  } else {
    updatedFilters[filterIndex] = newFilter
  }

  return updatedFilters
}

export const updatePagination = ({
  limit,
  offset,
  newPage,
  newPerPage,
}: {
  limit: IRequestPagination['limit']
  offset: IRequestPagination['offset']
  newPage?: number
  newPerPage?: number
}): IRequestPagination => {
  const newOffset = newPage === 1 || newPerPage ? 0 : newPage ? (newPage - 1) * limit : offset
  const newLimit = newPerPage ?? limit

  return { limit: newLimit, offset: newOffset }
}

export const updateSort = <T>(value: ISort<T>[]): SortT<T> => {
  const sortType = value[0].sortType
  const propertyName = value[0].propertyName

  return { propertyName, sortType }
}

export const changeFilterStateToDropdownOption = <T>(
  filters: IFilter<T>[],
  options: IDropdownOption[],
  id: T
): IDropdownOption[] => {
  return filters
    .filter((filter) => filter.id === id)
    .flatMap((filter) => {
      const filterValues =
        typeof filter.value === 'string' && filter.value.includes(',') ? filter.value.split(',') : filter.value

      return Array.isArray(filterValues)
        ? filterValues
            .map((value) => options.find((option) => option.value === value))
            .filter((option): option is IDropdownOption => !!option)
        : options.find((option) => option.value === filterValues)
    })
    .filter((option): option is IDropdownOption => !!option)
}

// EXAMPLE PAYLOAD
//
// const INIT_PAGINATION: IRequestPagination = { limit: 20, offset: 0 }
// const INIT_FILTERS: FilterT<IGetPurchaseListFilterProperties> = [
//   { id: 'createdAt', value: ['2023-04-22T13:15:42.000Z', '2023-04-25T13:15:42.000Z'], isDate: true },
//   { id: 'buyerIdentity.id', value: ['test id', '2'], filterVariant: FilterVariantE.IN },
//   { id: 'buyerIdentity.id', value: 'test id', filterVariant: FilterVariantE.IN },
// ]
// const INIT_SORT: SortT<IGetPurchaseListSortProperties> = { propertyName: 'purchaseType', sortType: 'asc' }
//
// const INIT_PAYLOAD: IBasePayload<IGetPurchaseListFilterProperties, IGetPurchaseListSortProperties> = {
//   filters: INIT_FILTERS,
//   sort: INIT_SORT,
//   pagination: INIT_PAGINATION,
// }
